import React, { useEffect, useState } from 'react';
import { Activity, BookOpen, TrendingUp, MessageSquare, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    activities: 0,
    resources: 0,
    selfImprovement: 0,
    contacts: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [activities, resources, selfImprovement, contacts] = await Promise.all([
        axios.get(`${API}/activities`),
        axios.get(`${API}/resources`),
        axios.get(`${API}/self-improvement`),
        axios.get(`${API}/admin/contacts`)
      ]);

      setStats({
        activities: activities.data.length,
        resources: resources.data.length,
        selfImprovement: selfImprovement.data.length,
        contacts: contacts.data.filter(c => c.status === 'new').length
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Activities',
      value: stats.activities,
      icon: Activity,
      color: 'from-blue-400 to-teal-500',
      link: '/admin/activities'
    },
    {
      title: 'Resources',
      value: stats.resources,
      icon: BookOpen,
      color: 'from-teal-400 to-green-500',
      link: '/admin/resources'
    },
    {
      title: 'Self-Improvement',
      value: stats.selfImprovement,
      icon: TrendingUp,
      color: 'from-green-400 to-cyan-500',
      link: '/admin/self-improvement'
    },
    {
      title: 'New Contacts',
      value: stats.contacts,
      icon: MessageSquare,
      color: 'from-orange-400 to-red-500',
      link: '/admin/contacts'
    },
    {
      title: 'Sales Dashboard',
      value: '→',
      icon: DollarSign,
      color: 'from-emerald-400 to-green-600',
      link: '/admin/sales'
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-xl text-gray-600">Loading dashboard...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <h1 className="text-4xl font-bold mb-2">Dashboard</h1>
        <p className="text-gray-600 mb-8">Welcome to the DadConnect Admin Panel</p>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <a href={stat.link} key={index}>
                <Card className="border-2 hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div className="text-3xl font-bold text-gray-900">{stat.value}</div>
                    </div>
                    <p className="text-gray-600 font-medium">{stat.title}</p>
                  </CardContent>
                </Card>
              </a>
            );
          })}
        </div>

        {/* Quick Actions */}
        <Card className="border-2">
          <CardHeader>
            <h2 className="text-2xl font-bold">Quick Actions</h2>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a
                href="/admin/activities"
                className="p-4 border-2 border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <Activity className="h-8 w-8 text-blue-600 mb-2" />
                <h3 className="font-bold mb-1">Add Activity</h3>
                <p className="text-sm text-gray-600">Create a new activity for dads</p>
              </a>
              <a
                href="/admin/resources"
                className="p-4 border-2 border-teal-200 rounded-lg hover:bg-teal-50 transition-colors"
              >
                <BookOpen className="h-8 w-8 text-teal-600 mb-2" />
                <h3 className="font-bold mb-1">Add Resource</h3>
                <p className="text-sm text-gray-600">Create parenting resource</p>
              </a>
              <a
                href="/admin/contacts"
                className="p-4 border-2 border-orange-200 rounded-lg hover:bg-orange-50 transition-colors"
              >
                <MessageSquare className="h-8 w-8 text-orange-600 mb-2" />
                <h3 className="font-bold mb-1">View Contacts</h3>
                <p className="text-sm text-gray-600">Review contact submissions</p>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;